import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Filter, AlertTriangle, Link2, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  efAdminSrList, efAdminSrDetail, efAdminSrPatch,
  efAdminSrAttachReplacement, efAdminSrQuickFilter,
} from "../../lib/api";

const STATUS_LIST = ["trusted", "under_review", "weak", "disputed", "replace_required"];

const STATUS_CLS = {
  trusted:         "text-[#5DD4B3] border-[#5DD4B3]/40",
  under_review:    "text-[#9FA8D4] border-[#9FA8D4]/40",
  weak:            "text-[#E0A526] border-[#E0A526]/40",
  disputed:        "text-[#E0A526] border-[#E0A526]/40",
  replace_required:"text-[#D92D20] border-[#D92D20]/40 bg-[#1a0808]",
};

const QUICK_FILTERS = [
  { key: "replace_required",                       label: "Replace required" },
  { key: "weak_sources_backing_public_items",       label: "Weak · public deps" },
  { key: "disputed_sources_backing_public_items",   label: "Disputed · public deps" },
  { key: "replacement_pending_review",              label: "Replacement pending" },
  { key: "blocked_from_republish",                  label: "Blocked from republish" },
  { key: "under_review",                            label: "Under review" },
];

export const AdminEpsteinSourceReliability = ({ token, userToken = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterText, setFilterText] = useState("");
  const [qf, setQf] = useState("");
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setQf("");
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterText) params.q = filterText;
      const r = await efAdminSrList(token, params, userToken);
      setRows(r.sources || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, [token, userToken, filterStatus, filterText]);

  useEffect(() => { load(); }, [load]);

  const applyQuickFilter = async (key) => {
    setLoading(true);
    setQf(key);
    try {
      const r = await efAdminSrQuickFilter(token, key, userToken);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick filter failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="ef-admin-sr-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">
            // SOURCE RELIABILITY
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Source reliability workflow</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Flag sources as weak, disputed, or replace-required. Attach replacement sources before
            dependent items can return to public.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="ef-sr-filter-status"
            className="border border-[#454038] bg-[#0F0F0F] text-[#A39E93] px-2 py-2 font-mono text-[10px] uppercase">
            <option value="">All statuses</option>
            {STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={filterText} onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="search title/url/publisher"
            data-testid="ef-sr-filter-text"
            className="bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-2 font-mono text-xs min-w-[14rem]" />
          <button onClick={load} data-testid="ef-sr-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 flex flex-wrap items-center gap-2"
           data-testid="ef-sr-quick-filters">
        <Filter size={11} className="text-[#CBB892]" />
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">quick filters</span>
        {QUICK_FILTERS.map((f) => (
          <button key={f.key} onClick={() => applyQuickFilter(f.key)}
            data-testid={`ef-sr-qf-${f.key}`}
            className={"font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-1 " +
              (qf === f.key ? "text-[#D92D20] border-[#D92D20]"
                            : "text-[#A39E93] border-[#454038] hover:border-[#D92D20]")}>
            {f.label}
          </button>
        ))}
        {qf && (
          <button onClick={load}
            className="ml-auto font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3]">
            clear filter
          </button>
        )}
      </div>

      <div className="border border-[#2D2A26] bg-[#131313]">
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
               data-testid="ef-sr-empty">No matches.</div>
        )}
        {rows.map((r) => {
          const cls = STATUS_CLS[r.reliability_status] || STATUS_CLS.trusted;
          const totalDeps = (r.public_relationships || 0) + (r.public_evidence_items || 0)
            + (r.public_fragments || 0) + (r.public_entities || 0);
          return (
            <div key={r.id} className="px-3 py-2 border-b border-[#2D2A26] flex flex-wrap items-center gap-2"
                 data-testid={`ef-sr-row-${r.id}`}>
              <span className={`font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 ${cls}`}>
                {r.reliability_status}
              </span>
              <span className="text-sm text-[#E4DFD3] truncate flex-1 min-w-[10rem]">{r.title}</span>
              {totalDeps > 0 && (
                <span className="font-mono text-[9px] text-[#CBB892] border border-[#454038] px-1.5 py-0.5">
                  {totalDeps} pub deps
                </span>
              )}
              {r.replacement_source_id && (
                <span className="font-mono text-[9px] text-[#5DD4B3] border border-[#5DD4B3]/40 px-1.5 py-0.5 flex items-center gap-1">
                  <Link2 size={9} /> replacement attached
                </span>
              )}
              {r.requires_replacement_before_republish && (
                <span className="font-mono text-[9px] text-[#D92D20] border border-[#D92D20]/40 px-1.5 py-0.5">
                  republish blocked
                </span>
              )}
              <span className="font-mono text-[9px] text-[#757065] truncate max-w-[16rem]">{r.url}</span>
              <button onClick={() => setActionId(r.id)}
                data-testid={`ef-sr-action-${r.id}`}
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20]/60 hover:bg-[#1a0808] px-2 py-1">
                Actions
              </button>
            </div>
          );
        })}
      </div>

      {actionId && (
        <SourceModal sourceId={actionId} token={token} userToken={userToken}
          onClose={() => setActionId(null)} onSaved={() => { setActionId(null); load(); }} />
      )}
    </div>
  );
};


const SourceModal = ({ sourceId, token, userToken, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("trusted");
  const [reason, setReason] = useState("");
  const [replId, setReplId] = useState("");
  const [replNotes, setReplNotes] = useState("");
  const [requires, setRequires] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await efAdminSrDetail(token, sourceId, userToken);
        if (!alive) return;
        setDetail(r);
        setStatus(r.reliability_status || "trusted");
        setReason(r.reliability_reason || "");
        setReplId(r.replacement_source_id || "");
        setReplNotes(r.replacement_notes || "");
        setRequires(!!r.requires_replacement_before_republish);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Detail failed");
      }
    })();
    return () => { alive = false; };
  }, [sourceId, token, userToken]);

  const save = async () => {
    if (status !== "trusted" && reason.trim().length < 4) {
      toast.error("Reason of 4+ chars required when status is not trusted"); return;
    }
    if (replId && replId === sourceId) {
      toast.error("Replacement source cannot equal source itself"); return;
    }
    setBusy(true);
    try {
      const payload = {
        reliability_status: status,
        reliability_reason: reason || undefined,
        replacement_source_id: replId || null,
        replacement_notes: replNotes || null,
        requires_replacement_before_republish: requires,
      };
      await efAdminSrPatch(token, sourceId, payload, userToken);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const attachReplacement = async () => {
    if (!replId || replId === sourceId) { toast.error("Pick a different replacement source id"); return; }
    setBusy(true);
    try {
      await efAdminSrAttachReplacement(token, {
        source_id: sourceId, replacement_source_id: replId,
        replacement_notes: replNotes || undefined, require_before_republish: requires,
      }, userToken);
      toast.success("Replacement attached");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setBusy(false); }
  };

  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
         data-testid="ef-sr-modal">
      <div className="bg-[#0F0F0F] border border-[#D92D20]/40 max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
              // source reliability
            </div>
            <div className="font-display text-lg text-[#E4DFD3]">{detail.title}</div>
            <div className="font-mono text-[10px] text-[#757065] mt-1 break-all">{detail.url}</div>
          </div>
          <button onClick={onClose} className="text-[#757065] hover:text-[#E4DFD3]"
                  data-testid="ef-sr-modal-close"><X size={14} /></button>
        </div>

        {/* Dep counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {["public_relationships", "public_evidence_items", "public_fragments", "public_entities"].map((k) => (
            <div key={k} className="border border-[#2D2A26] bg-[#131313] px-2 py-1.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
                {k.replace(/_/g, " ")}
              </div>
              <div className="font-display text-xl text-[#E4DFD3]">{detail[k] || 0}</div>
            </div>
          ))}
        </div>

        {/* Drill-down */}
        {detail.dependencies && Object.entries(detail.dependencies).map(([cat, rows]) =>
          rows.length > 0 ? (
            <details key={cat} className="border border-[#2D2A26] bg-[#0F0F0F] mb-2">
              <summary className="px-2 py-1 cursor-pointer font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">
                {cat.replace(/_/g, " ")} · {rows.length}
              </summary>
              <div className="px-2 pb-2 text-xs text-[#A39E93]">
                {rows.map((d) => (
                  <div key={d.id} className="py-1 border-t border-[#1a1a1a] truncate">
                    {d.title} <span className="text-[#757065]">· {d.id}</span>
                  </div>
                ))}
              </div>
            </details>
          ) : null
        )}

        {detail.replacement_source && (
          <div className="border border-[#5DD4B3]/40 bg-[#0e1816] px-3 py-2 mb-3"
               data-testid="ef-sr-replacement-summary">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1">
              replacement attached
            </div>
            <div className="text-sm text-[#E4DFD3]">{detail.replacement_source.title}</div>
            <div className="font-mono text-[10px] text-[#757065] break-all">{detail.replacement_source.url}</div>
          </div>
        )}

        {/* Edit form */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">status</label>
            <select value={status} onChange={(e) => {
              setStatus(e.target.value);
              if (e.target.value === "replace_required") setRequires(true);
            }} data-testid="ef-sr-status-select"
              className="w-full bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono">
              {STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">replacement source id</label>
            <input value={replId} onChange={(e) => setReplId(e.target.value)}
              placeholder="efx_…"
              data-testid="ef-sr-repl-id"
              className="w-full bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono" />
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              data-testid="ef-sr-reason"
              className="w-full bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono" />
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">replacement notes</label>
            <textarea value={replNotes} onChange={(e) => setReplNotes(e.target.value)} rows={2}
              data-testid="ef-sr-repl-notes"
              className="w-full bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono" />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">
            <input type="checkbox" checked={requires} onChange={(e) => setRequires(e.target.checked)}
              data-testid="ef-sr-requires-gate" className="accent-[#D92D20]" />
            Require replacement before republish
          </label>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <button onClick={attachReplacement} disabled={busy || !replId}
            data-testid="ef-sr-attach-replacement"
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#5DD4B3]/60 text-[#5DD4B3] hover:bg-[#0e1816] px-3 py-2 disabled:opacity-40 flex items-center gap-1">
            <Link2 size={11} /> Attach replacement
          </button>
          <button onClick={save} disabled={busy}
            data-testid="ef-sr-save"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] border border-[#D92D20] hover:bg-[#1a0808] px-3 py-2 disabled:opacity-40 flex items-center gap-1">
            <Save size={11} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEpsteinSourceReliability;
